import React, { Fragment } from "react";
import { UserIcon } from "@heroicons/react/24/solid";
import { useAuth } from "./context/AuthContext";


import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  ShoppingBagIcon,
} from "@heroicons/react/24/outline";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "./api/axios";
import { useCart } from "./context/CartContext";
import LanguageSelector from "../i18n/LanguageSelector";
import { useI18n } from "../i18n/I18nContext";

const clientNavigation = [
  { name: "Loja", href: "/products" },
  { name: "Sobre Nós", href: "/about" },
  { name: "Parcerias", href: "/partnerships" },
  { name: "Comunicados", href: "/press-releases" },
  { name: "Catálogo", href: "/catalogo" },
  { name: "Contactos", href: "/contact" },
];

const adminNavigation = [
  { name: "Utilizadores", href: "/admin/users" },
  { name: "Produtos", href: "/admin/products" },
  { name: "Categorias", href: "/admin/categories" },
  { name: "Encomendas", href: "/admin/orders" },
  { name: "Cupões", href: "/admin/coupons" },

];


function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();

  const {
    items, totalItems, totalPrice,
    removeItem, clear, decrement, addItem,
    isCartOpen, openCart, closeCart, resetCart,
  } = useCart();

  const location = useLocation();
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const isAdmin = user?.roles?.some((r) => r.name === "admin");
  const logoHref = token && isAdmin ? "/admin/users" : "/";

  const isAdminRoute = location.pathname.startsWith("/admin");
  const navToRender = token && isAdmin && isAdminRoute ? adminNavigation : clientNavigation;

  const handleLogout = async () => {
    try {
      await api.post("/api/v1/logout");
    } catch (err) {
      console.error("Erro no logout:", err?.response || err?.message);
    } finally {
      closeCart();
      resetCart();
      localStorage.removeItem("cart");
      logout();
      navigate("/login");
    }
  };

  const navLinkClass = (current) =>
    classNames(
      "px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-200",
      current
        ? "bg-neutral-900 text-white"
        : "text-neutral-800 hover:bg-neutral-900/5 hover:text-neutral-900"
    );

  const iconBtnClass =
    "relative rounded-full p-2 text-neutral-700 hover:bg-neutral-900/5 transition-colors duration-200 focus:outline-none";

  return (
    <Disclosure as="nav" className="fixed top-0 left-0 right-0 z-30 bg-white shadow-sm border-b border-gray-100">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between">

          <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
            <DisclosureButton className="group relative inline-flex items-center justify-center rounded-lg p-2 text-neutral-800 hover:bg-neutral-900/5 focus:outline-none transition-colors">
              <span className="absolute -inset-0.5" />
              <span className="sr-only">{t("Abrir menu principal")}</span>
              <Bars3Icon className="block size-6 group-data-open:hidden" />
              <XMarkIcon className="hidden size-6 group-data-open:block" />
            </DisclosureButton>
          </div>

          <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
            <Link to={logoHref} className="flex shrink-0 items-center gap-2.5">
              <img alt="RacksForAll Logo" src="/racksforall.png" className="h-8 w-auto" />
              <span className="hidden sm:block font-bold text-xl tracking-tight text-neutral-900">
                RacksForAll
              </span>
            </Link>

            <div className="hidden sm:ml-8 sm:flex sm:items-center sm:gap-1">
              {navToRender.map((item) => {
                const current = location.pathname === item.href || location.pathname.startsWith(item.href + "/");
                return (
                  <Link key={item.name} to={item.href} className={navLinkClass(current)}>
                    {t(item.name)}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
            <LanguageSelector />

            {!isAdminRoute && (
              <button type="button" onClick={openCart} className={iconBtnClass}>
                <ShoppingBagIcon className="size-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-600 text-white text-[10px] font-semibold flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>
            )}

            {!token ? (
              <button
                onClick={() => navigate("/login")}
                className="ml-1 rounded-full bg-neutral-900 px-4 py-1.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-neutral-700"
              >
                {t("Entrar")}
              </button>
            ) : (
              <Menu as="div" className="relative ml-1">
                <MenuButton className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium text-neutral-800 transition-colors duration-200 hover:bg-neutral-900/5 focus:outline-none">
                  <UserIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("Minha conta")}</span>
                </MenuButton>

                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 transition data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                >
                  <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                    {user?.name || t("Utilizador")} · {user?.email || ""}
                  </div>
                  <MenuItem>
                    <button onClick={() => navigate("/profile")} className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      {t("Perfil")}
                    </button>
                  </MenuItem>
                  {token && !isAdmin && (
                    <MenuItem>
                      <button onClick={() => navigate("/orders")} className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        {t("Encomendas")}
                      </button>
                    </MenuItem>
                  )}
                  {token && !isAdmin && (
                    <MenuItem>
                      <button onClick={() => navigate("/wishlist")} className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        {t("Wishlist")}
                      </button>
                    </MenuItem>
                  )}
                  <div className="border-t border-gray-100 mt-1">
                    <MenuItem>
                      <button onClick={handleLogout} className="w-full text-left block px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                        {t("Sair")}
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            )}
          </div>
        </div>
      </div>

      <div className="h-px w-full bg-gradient-to-r from-transparent via-black/10 to-transparent" />

      <DisclosurePanel className="sm:hidden border-t bg-white border-black/5">
        <div className="space-y-1 px-4 pt-3 pb-4">
          {navToRender.map((item) => {
            const current = location.pathname === item.href || location.pathname.startsWith(item.href + "/");
            return (
              <DisclosureButton
                key={item.name}
                as={Link}
                to={item.href}
                className={classNames(
                  "block w-full rounded-full px-3 py-2 text-sm font-medium transition-colors",
                  current
                    ? "bg-neutral-900 text-white"
                    : "text-neutral-800 hover:bg-neutral-900/5 hover:text-neutral-900"
                )}
              >
                {t(item.name)}
              </DisclosureButton>
            );
          })}

          {!token ? (
            <DisclosureButton
              as={Link}
              to="/login"
              className="block w-full rounded-full px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-900/5 transition-colors"
            >
              {t("Entrar")}
            </DisclosureButton>
          ) : (
            <DisclosureButton
              as="button"
              onClick={handleLogout}
              className="w-full text-left block rounded-full px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              {t("Sair")}
            </DisclosureButton>
          )}
        </div>
      </DisclosurePanel>

      <Transition show={isCartOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeCart}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <TransitionChild
                  as={Fragment}
                  enter="transform transition ease-out duration-300"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in duration-200"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <DialogPanel className="pointer-events-auto w-screen max-w-md">
                    <div className="flex h-full flex-col bg-white shadow-xl">
                      <div className="flex items-center justify-between px-4 py-4 border-b">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">{t("Carrinho")}</h2>
                          <p className="text-sm text-gray-500">{t(`${totalItems} item(s)`)}</p>
                        </div>
                        <button onClick={closeCart} className="rounded-md p-2 text-gray-500 hover:bg-gray-100">
                          <XMarkIcon className="size-6" />
                        </button>
                      </div>

                      <div className="flex-1 overflow-y-auto px-4 py-4">
                        {items?.length ? (
                          <div className="space-y-4">
                            {items.map((item) => (
                              <div key={item.id} className="flex gap-4 border-b pb-4">
                                <div
                                  className="w-16 h-16 bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center cursor-pointer"
                                  onClick={() => navigate(`/products/${item.product_id}`)}
                                >
                                  {item.image_url ? (
                                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover hover:opacity-80 transition" />
                                  ) : (
                                    <span className="text-gray-400 text-xs">{t("Sem imagem")}</span>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{item.name}</p>
                                  {item.variant_type && <p className="text-xs text-gray-500">{t("Tipo")}: {item.variant_type}</p>}
                                  {item.variant_label && <p className="text-xs text-gray-500">{t("Variante")}: {item.variant_label}</p>}
                                  <p className="text-sm text-indigo-600 font-medium">€ {Number(item.price).toFixed(2)}</p>
                                  <div className="mt-2 flex items-center gap-2">
                                    <button onClick={() => decrement(item.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold">−</button>
                                    <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                                    <button
                                      onClick={() => addItem({
                                        id: item.id,
                                        product_id: item.product_id,
                                        name: item.name,
                                        price: item.price,
                                        image_url: item.image_url ?? null,
                                        quantity: 1,
                                        variant_id: item.variant_id,
                                        variant_label: item.variant_label,
                                        variant_type: item.variant_type,
                                      })}
                                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold"
                                    >+</button>
                                    <button onClick={() => removeItem(item.id)} className="ml-auto text-xs text-red-500 hover:text-red-700">
                                      {t("Remover")}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 mt-10 gap-3">
                            <ShoppingBagIcon className="w-12 h-12 text-gray-200" />
                            <p className="text-sm">{t("O carrinho está vazio.")}</p>
                          </div>
                        )}
                      </div>

                      <div className="border-t px-4 py-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-gray-600 text-sm">{t("Total")}</span>
                          <span className="text-gray-900 font-bold text-lg">€ {Number(totalPrice || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={clear}
                            disabled={!items?.length}
                            className="w-1/3 rounded-xl bg-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-40"
                          >
                            {t("Limpar")}
                          </button>
                          <button
                            onClick={() => {
                              closeCart();

                              if (!token) {
                                navigate("/login", { state: { from: "/checkout" } });
                                return;
                              }

                              navigate("/checkout");
                            }}
                            disabled={!items?.length}
                            className="w-2/3 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40 shadow-sm"
                          >
                            {t("Finalizar encomenda")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Disclosure>
  );
}
